import React, { useCallback, useEffect, useState } from "react";
import { MdManageAccounts } from "react-icons/md";
import { AcessoOrganizacao } from "../../../interfaces/acessos";
import { getOrganizacoesAcesso } from "../../../services/acessosApi";
import { Carregando, Erro, INPUT, LABEL, Vazio } from "../paineis-admin/components/shared";
import HistoricoTab from "./components/HistoricoTab";
import PerfisTab from "./components/PerfisTab";
import PessoasTab from "./components/PessoasTab";

type Aba = "pessoas" | "perfis" | "historico";

const ABAS: { id: Aba; rotulo: string }[] = [
  { id: "pessoas", rotulo: "Pessoas" },
  { id: "perfis", rotulo: "Perfis" },
  { id: "historico", rotulo: "Histórico" },
];

/**
 * Pessoas de uma organização cliente: convidar, trocar perfil, desativar.
 * Visível a `identidade.admin` (qualquer organização) e `org.people.manage` (a própria) — quem
 * decide de fato é o backend; aqui só se desenha o que ele devolveu em `GET acessos/organizacoes`.
 */
const AcessosPage: React.FC = () => {
  const [organizacoes, setOrganizacoes] = useState<AcessoOrganizacao[]>([]);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [aba, setAba] = useState<Aba>("pessoas");
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const lista = await getOrganizacoesAcesso();
      setOrganizacoes(lista);
      setOrgId((atual) => atual ?? lista[0]?.id ?? null);
      setErro(false);
    } catch {
      setErro(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <div className="mt-5 animate-fade-up">
      <header className="mb-6 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span
            className="dv-aura flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow"
            aria-hidden="true"
          >
            <MdManageAccounts className="h-6 w-6" />
          </span>
          <div>
            <span className="inline-flex items-center rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
              Cadastros
            </span>
            <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
              Acessos
            </h1>
          </div>
        </div>

        {organizacoes.length > 0 && (
          <div className="w-full sm:w-72">
            {organizacoes.length === 1 ? (
              <span id="acessos-organizacao-rotulo" className={LABEL}>
                Organização
              </span>
            ) : (
              <label htmlFor="acessos-organizacao" className={LABEL}>
                Organização
              </label>
            )}
            {organizacoes.length === 1 ? (
              <p
                aria-labelledby="acessos-organizacao-rotulo"
                className="mt-1 truncate rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white"
                title={organizacoes[0].nome}
              >
                {organizacoes[0].nome}
              </p>
            ) : (
              <select
                id="acessos-organizacao"
                className={`${INPUT} mt-1`}
                value={orgId ?? ""}
                onChange={(e) => setOrgId(Number(e.target.value))}
              >
                {organizacoes.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nome}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </header>

      {loading && <Carregando rotulo="Carregando organizações" />}
      {!loading && erro && (
        <Erro texto="Não foi possível carregar as organizações. Tente de novo." onTentar={carregar} />
      )}
      {!loading && !erro && orgId === null && (
        <Vazio texto="Você não administra pessoas de nenhuma organização." />
      )}

      {!loading && !erro && orgId !== null && (
        <>
          <div
            role="tablist"
            aria-label="Seções de acessos"
            className="mb-5 flex gap-1 overflow-x-auto rounded-2xl border border-gray-100 bg-white p-1 dark:border-white/10 dark:bg-navy-800"
          >
            {ABAS.map((a) => {
              const ativa = a.id === aba;
              return (
                <button
                  key={a.id}
                  type="button"
                  role="tab"
                  id={`tab-${a.id}`}
                  aria-selected={ativa}
                  aria-controls={`painel-${a.id}`}
                  onClick={() => setAba(a.id)}
                  className={`whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 ${
                    ativa
                      ? "bg-ink-900 text-white dark:bg-white dark:text-ink-900"
                      : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5"
                  }`}
                >
                  {a.rotulo}
                </button>
              );
            })}
          </div>

          <div role="tabpanel" id={`painel-${aba}`} aria-labelledby={`tab-${aba}`}>
            {aba === "pessoas" && <PessoasTab key={orgId} orgId={orgId} />}
            {aba === "perfis" && <PerfisTab key={orgId} orgId={orgId} />}
            {aba === "historico" && <HistoricoTab key={orgId} orgId={orgId} />}
          </div>
        </>
      )}
    </div>
  );
};

export default AcessosPage;
